/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * RoleContext - Manages user job roles (CO, PM, Hybrid) and admin roles
 * 
 * Job Roles:
 * - co: Contracting Officer - handles business/legal side, signatures, compliance
 * - pm: Program Manager - owns technical side, requirements, deliverables
 * - hybrid: Both roles combined - sees everything
 * 
 * Admin Roles:
 * - super_admin: Full system access, can switch between all role views
 * - admin: Agency admin, manages their team
 * - user: Regular user (CO/PM/Hybrid)
 */

const RoleContext = createContext();

export const JOB_ROLES = {
  CO: 'co',
  PM: 'pm',
  HYBRID: 'hybrid'
};

export const ADMIN_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  USER: 'user'
};

export const JOB_ROLE_LABELS = {
  co: 'Contracting Officer',
  pm: 'Program Manager',
  hybrid: 'Hybrid (CO + PM)'
};

export const JOB_ROLE_SHORT = {
  co: 'CO',
  pm: 'PM',
  hybrid: 'Hybrid'
};

export const VIEW_AS_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin View', icon: '👑' },
  { value: 'admin', label: 'Agency Admin View', icon: '' },
  { value: 'hybrid', label: 'Hybrid (CO + PM)', icon: '' },
  { value: 'co', label: 'Contracting Officer', icon: '' },
  { value: 'pm', label: 'Program Manager', icon: '' },
];

export function RoleProvider({ children }) {
  const [jobRole, setJobRole] = useState('hybrid');
  const [viewAsRole, setViewAsRole] = useState(null); // For super admin role switching
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [demoPersona, setDemoPersona] = useState(null); // For demo mode personas

  // Function to load user data from localStorage
  const loadUserData = () => {
    const user = JSON.parse(localStorage.getItem('asp_user') || '{}');
    if (user.job_role) {
      setJobRole(user.job_role);
    }
    const superAdmin = user.is_super_admin || user.role === 'super_admin';
    setIsSuperAdmin(superAdmin);
    setIsAdmin(user.role === 'admin' || user.role === 'super_admin');
    
    // Check for demo persona (set during login for demo testers)
    if (user.persona) {
      setDemoPersona(user.persona);
    } else {
      setDemoPersona(null);
    }
    
    // Load saved viewAs preference for super admin
    if (superAdmin) {
      const savedViewAs = localStorage.getItem('asp_view_as');
      if (savedViewAs) {
        setViewAsRole(savedViewAs);
      }
    } else {
      setViewAsRole(null);
    }
    setIsLoading(false);
  };

  // Load on mount
  useEffect(() => {
    loadUserData();
  }, []);

  // Listen for storage changes (for login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'asp_user' || e.key === null) {
        loadUserData();
      }
    };
    
    // Listen for custom login event
    const handleLogin = () => {
      loadUserData();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogin', handleLogin);
    
    // Also check periodically in case storage event doesn't fire (same tab)
    const interval = setInterval(() => {
      const user = JSON.parse(localStorage.getItem('asp_user') || '{}');
      const currentSuperAdmin = user.is_super_admin || user.role === 'super_admin';
      if (currentSuperAdmin !== isSuperAdmin) {
        loadUserData();
      }
    }, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleLogin);
      clearInterval(interval);
    };
  }, [isSuperAdmin]);

  // Switch view as (super admin only)
  const switchViewAs = (newRole) => {
    if (!isSuperAdmin) return;
    
    setViewAsRole(newRole);
    localStorage.setItem('asp_view_as', newRole);
  };

  // Clear view as (return to super admin view)
  const clearViewAs = () => {
    setViewAsRole(null);
    localStorage.removeItem('asp_view_as');
  };

  // Get the effective role (considering viewAs for super admin) - memoized
  const getEffectiveRoleMemo = useCallback(() => {
    if (isSuperAdmin && viewAsRole) {
      return viewAsRole;
    }
    return jobRole;
  }, [isSuperAdmin, viewAsRole, jobRole]);

  // Check if user has access to a specific role's features - memoized with useCallback
  const hasAccess = useCallback((requiredRole) => {
    const effectiveRole = getEffectiveRoleMemo();
    
    // Super admin sees everything when not in viewAs mode
    if (isSuperAdmin && !viewAsRole) return true;
    
    // When viewing as a specific role, respect that role's access
    if (effectiveRole === 'super_admin' || effectiveRole === 'admin') return true;
    if (effectiveRole === 'hybrid') return true;
    if (requiredRole === 'shared') return true;
    
    return effectiveRole === requiredRole;
  }, [isSuperAdmin, viewAsRole, getEffectiveRoleMemo]);

  // Check if user can switch roles (only super admin can)
  const canSwitchRoles = () => {
    return isSuperAdmin;
  };

  // Get the actual job role (not the view preference)
  const getActualRole = () => {
    const user = JSON.parse(localStorage.getItem('asp_user') || '{}');
    return user.job_role || 'hybrid';
  };

  const value = {
    jobRole,
    setJobRole,
    viewAsRole,
    switchViewAs,
    clearViewAs,
    hasAccess,
    canSwitchRoles,
    getActualRole,
    getEffectiveRole: getEffectiveRoleMemo,
    isLoading,
    isSuperAdmin,
    isAdmin,
    isCO: getEffectiveRoleMemo() === 'co',
    isPM: getEffectiveRoleMemo() === 'pm',
    isHybrid: getEffectiveRoleMemo() === 'hybrid',
    // Demo mode support
    demoPersona,
    isDemoMode: !!demoPersona,
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}

export default RoleContext;
